import React, { useContext, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { useParams } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import Container from 'components/Container'
import Row from 'components/Row'
import Col from 'components/Col'
import Input from 'components/Input'
import Select from 'components/Select'
import Textarea from 'components/Textarea'
import { delay } from 'utils/delay'
import { requestUpdateSiteEcoSystem } from 'api/ecoSystem'
import { redListOfEcosystemCategory } from 'constants/redListOfEcosystemCategory'
import { defaultSiteProposalValidationResults } from 'constants/defaultSiteProposalValidationResults'
import { SiteContext } from 'providers/site'
import { SITE_STATUS_DRAFT_ID } from 'constants/siteStatuses'
import { reasonDefaultOptions } from 'constants/rejectTriggerReason'
import { EcosystemContentFooter } from './style'
import MultiSelectionDropdown from 'components/multiSelectDropdown'
import CheckIcon from 'assets/icons/check-icon'
import Button from 'components/Button'
import formatNum2Dp from 'utils/formatNum2DP'
import useEcosystemPersistence from 'hooks/useEcosystemPersistence'
import { isAllowedAssessmentDateInput, isPersistableAssessmentDate, isValidEcosystemDecimalInput } from 'validators/ecosystemValidators'
import {
  getRelationshipFieldError,
  validateBestSiteAgainstGlobalExtent,
  validateSiteExtentOrder,
  withRelationshipError
} from 'validators/estimateRelationshipValidators'
import { getCheckedReasonOptions, mapTriggerReasonFromApi, mapTriggerReasonToApi, toFixedNumberOrZero } from 'utils/ecosystemDataHelpers'

const inputSaveDelayTime = 300
const updateEcosystemNameTimeOut = delay(inputSaveDelayTime)
// const updateEcosystemNumberTimeOut = delay(inputSaveDelayTime)
const updateEcosystemGlobalExtentTimeOut = delay(inputSaveDelayTime)
const updateEcosystemExtentAtSiteMinTimeOut = delay(inputSaveDelayTime)
const updateEcosystemExtentAtSiteBestTimeOut = delay(inputSaveDelayTime)
const updateEcosystemExtentAtSiteMaxTimeOut = delay(inputSaveDelayTime)
const updateEcosystemAssessmentDateTimeOut = delay(inputSaveDelayTime)
const updateEcosystemDataSourceTimeOut = delay(inputSaveDelayTime)

const siteExtentFields = {
  min: 'extentAtSiteMin',
  best: 'extentAtSiteBest',
  max: 'extentAtSiteMax'
}

const bestExtentFields = {
  bestSite: 'extentAtSiteBest',
  globalExtent: 'globalExtent'
}

const ecosystemPropType = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  name: PropTypes.string,
  EcoSystemRedListId: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  globalExtent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  extentAtSiteMin: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  extentAtSiteBest: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  extentAtSiteMax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  assessmentDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.oneOf([null])]),
  dataSource: PropTypes.string,
  triggerReasonId: PropTypes.arrayOf(PropTypes.number),
  triggerReasonText: PropTypes.string,
  reassessmentFlag: PropTypes.bool,
  lastReassessmentDate: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  SiteStatusId: PropTypes.number
})

const EcosystemReassessmentRationale = ({ ecosystem, site, reassessmentRational, handleChangeReassessmentRational, disabled }) => {
  if (ecosystem.lastReassessmentDate === null || !site.reassessmentFlag) {
    return null
  }

  return (
    <Row>
      <Col md={12}>
        <Textarea
          value={reassessmentRational}
          onChange={handleChangeReassessmentRational}
          placeholder='justificationNoChangeEcoSystemPlaceholder'
          label='justificationNoChange'
          infoTooltip='qualificationReasonHint'
          field='rationalForReassessment'
          height='128px'
          disabled={disabled}
        />
      </Col>
    </Row>
  )
}

const EcosystemFormFooter = ({ ecosystem, site, defaultPage, toggleAccordion }) => {
  if (site.SiteStatusId !== SITE_STATUS_DRAFT_ID || defaultPage) {
    return null
  }

  const hasTriggerReasons = ecosystem?.triggerReasonId?.length > 0
  let buttonType = 'primary'
  if (ecosystem?.reassessmentFlag && !hasTriggerReasons) {
    buttonType = 'disabledPrimary'
  }
  const isDisabled = ecosystem?.reassessmentFlag ? !hasTriggerReasons : false

  return (
    <Row>
      <Col md={12}>
        <EcosystemContentFooter>
          <Button
            height='40px'
            isDisabled={isDisabled}
            onClick={toggleAccordion}
            type={buttonType}
          >
            <CheckIcon />
            <FormattedMessage id='finishEditing' />
          </Button>
        </EcosystemContentFooter>
      </Col>
    </Row>
  )
}

const EcosystemForm = ({ item: ecosystem, updateEcoSystem, siteId, toggleAccordion, defaultPage = false, getEcosystems }) => {
  const params = useParams()
  const checkedValue = useRef([])
  const { site, siteProposalFieldValidation, isEditable } = useContext(SiteContext)

  const checkFields = siteProposalFieldValidation?.ecoSystem?.[ecosystem.id]
    ? siteProposalFieldValidation.ecoSystem[ecosystem.id]
    : defaultSiteProposalValidationResults.ecoSystem


  const [ecoSystemName, setEcoSystemName] = useState(ecosystem.name)
  // const [codeNumber, setCodeNumber] = useState(ecosystem.number)
  const [ecosystemRedListId, setEcosystemRedListId] = useState(
    redListOfEcosystemCategory.find((item) => {
      return item.value === ecosystem.EcoSystemRedListId
    }) || null
  )

  const [globalExtent, setGlobalExtent] = useState(ecosystem.globalExtent ? formatNum2Dp(ecosystem.globalExtent) : '')
  const [extent, setExtent] = useState(ecosystem.extentAtSiteMin ? formatNum2Dp(ecosystem.extentAtSiteMin) : '')
  const [extentBest, setExtentBest] = useState(ecosystem.extentAtSiteBest ? formatNum2Dp(ecosystem.extentAtSiteBest) : '')
  const [extentMax, setExtentMax] = useState(ecosystem.extentAtSiteMax ? formatNum2Dp(ecosystem.extentAtSiteMax) : '')
  const [assessmentDate, setAssessmentDate] = useState(ecosystem.assessmentDate)
  const [dataSource, setDataSource] = useState(ecosystem.dataSource)
  const [qualificationReason, setQualificationReason] = useState(mapTriggerReasonFromApi(ecosystem.triggerReasonId))

  const [reassessmentRational, setReassessmentRational] = useState(ecosystem.triggerReasonText ? ecosystem.triggerReasonText : '')
  const [checkedOptions, setCheckedOptions] = useState(reasonDefaultOptions)

  const qualified = ecosystem.reassessmentFlag ? ecosystem.triggerReasonId.includes(1) : true

  const disabled = site.SiteStatusId !== SITE_STATUS_DRAFT_ID || (!isEditable && site.SiteStatusId === SITE_STATUS_DRAFT_ID) || !qualified
  const { persistField } = useEcosystemPersistence({
    ecosystemId: ecosystem.id,
    siteId,
    siteHistoryId: params.siteHistoryId,
    updateEcoSystem,
    getEcosystems
  })

  function handleChangeValueChecked(Ids) {
    checkedValue.current = Ids
    setCheckedOptions(getCheckedReasonOptions(Ids))
  }

  function handleChangeQualificationReason(e) {
    setQualificationReason(e)
    const formattedReasons = mapTriggerReasonToApi(e)
    requestUpdateSiteEcoSystem(ecosystem.id, params.siteHistoryId, { triggerReasonId: formattedReasons }).then((updatedEcoSystem) => {
      updateEcoSystem(updatedEcoSystem)
      getEcosystems()
    })
  }

  function handleChangeReassessmentRational(e) {
    setReassessmentRational(e.target.value)

    requestUpdateSiteEcoSystem(ecosystem.id, params.siteHistoryId, { triggerReasonText: e.target.value }).then((updatedEcoSystem) => {
      updateEcoSystem(updatedEcoSystem)
      getEcosystems()
    })
  }

  const handleChangeEcosystemName = (e) => {
    setEcoSystemName(e.target.value)
  }

  const handleOnBlurChangeEcosystemName = (e) => {
    setEcoSystemName(e.target.value)

    updateEcosystemNameTimeOut(() => {
      persistField({ name: e.target.value })
    })
  }

  function handleChangeEcosystemRedListId(e) {
    setEcosystemRedListId(e)

    persistField({ EcoSystemRedListId: e.value })
  }

  function handleChangeGlobalExtent(e) {
    if (isValidEcosystemDecimalInput(e.target.value)) {
      setGlobalExtent(e.target.value)
    }
  }

  function handleOnBlurChangeGlobalExtent(e) {
    if (isValidEcosystemDecimalInput(e.target.value)) {
      setGlobalExtent(e.target.value)
      const value = toFixedNumberOrZero(e.target.value)
      updateEcosystemGlobalExtentTimeOut(() => {
        persistField({ globalExtent: value })
      })
    }
  }

  function handleChangeExtent(e) {
    if (isValidEcosystemDecimalInput(e.target.value)) {
      setExtent(e.target.value)
    }
  }

  function handleOnBlurChangeExtent(e) {
    if (isValidEcosystemDecimalInput(e.target.value)) {
      setExtent(e.target.value)
      const value = toFixedNumberOrZero(e.target.value)
      updateEcosystemExtentAtSiteMinTimeOut(() => {
        persistField({ extentAtSiteMin: value })
      })
    }
  }

  function handleChangeExtentBest(e) {
    if (isValidEcosystemDecimalInput(e.target.value)) {
      setExtentBest(e.target.value)
    }
  }

  function handleOnBlurChangeExtentBest(e) {
    if (isValidEcosystemDecimalInput(e.target.value)) {
      setExtentBest(e.target.value)
      const value = toFixedNumberOrZero(e.target.value)
      updateEcosystemExtentAtSiteBestTimeOut(() => {
        persistField({ extentAtSiteBest: value })
      })
    }
  }

  function handleChangeExtentMax(e) {
    if (isValidEcosystemDecimalInput(e.target.value)) {
      setExtentMax(e.target.value)
    }
  }

  function handleOnBlurChangeExtentMax(e) {
    if (isValidEcosystemDecimalInput(e.target.value)) {
      setExtentMax(e.target.value)
      const value = toFixedNumberOrZero(e.target.value)
      updateEcosystemExtentAtSiteMaxTimeOut(() => {
        persistField({ extentAtSiteMax: value })
      })
    }
  }

  function handleChangeAssessmentDate(e) {
    const currentYear = new Date().getFullYear()
    const passedValue = e.target.value
    if (isAllowedAssessmentDateInput(passedValue, currentYear)) {
      setAssessmentDate(passedValue)
    }
    if (isPersistableAssessmentDate(passedValue, currentYear)) {
      updateEcosystemAssessmentDateTimeOut(() => {
        persistField({ assessmentDate: e.target.value ? e.target.value : null })
      })
    }
  }

  function persistDataSource(value) {
    updateEcosystemDataSourceTimeOut(() => {
      persistField({ dataSource: value })
    })
  }

  function handleChangeDataSource(e) {
    const { value } = e.target
    setDataSource(value)

    persistDataSource(value)
  }

  const handleOnBlurChangeDataSource = handleChangeDataSource

  // Both relationships are recomputed on every render, so editing any of the four extents flags the
  // affected fields immediately. The API applies the same rules and is what blocks the proposal.
  const extentRelationshipErrors = {
    ...validateBestSiteAgainstGlobalExtent(extentBest, globalExtent, bestExtentFields),
    ...validateSiteExtentOrder(extent, extentBest, extentMax, siteExtentFields)
  }

  return (
    <Container width='100%' padding='0px'>
      <div data-test='ecosystem-form'>
        <Row>
          <Col md={6}>
            <Input
              value={ecoSystemName}
              field='ecoSystemName'
              onChange={handleChangeEcosystemName}
              onBlur={handleOnBlurChangeEcosystemName}
              placeholder='enterEcosystemName'
              label='ecosystemName'
              infoTooltip='ecosystemNameHint'
              disabled={disabled}
              error={
                checkFields.name
                  ? { field: '', error: '' }
                  : {
                    field: 'ecoSystemName',
                    error: 'pleaseCompleteThisField'
                  }
              }
            />
          </Col>
          <Col md={6}>
            <Select
              value={ecosystemRedListId}
              onChange={handleChangeEcosystemRedListId}
              placeholder='selectEcosystemCategory'
              label='ecosystemCategory'
              field='EcoSystemRedListId'
              infoTooltip='ecosystemCategoryHint'
              options={redListOfEcosystemCategory}
              disabled={disabled}
              error={
                checkFields.EcoSystemRedListId
                  ? { field: '', error: '' }
                  : {
                    field: 'EcoSystemRedListId',
                    error: 'pleaseCompleteThisField'
                  }
              }
            />
          </Col>
        </Row>
        <Row>
          {(ecosystem.lastReassessmentDate !== null && site.reassessmentFlag && !defaultPage) && (
            <Col md={6}>
              <MultiSelectionDropdown
                value={qualificationReason}
                saveChanges={handleChangeQualificationReason}
                valueChecked={handleChangeValueChecked}
                label='triggerEcosystemLabel'
                infoTooltip='triggerLabelHint'
                field='qualificationReason'
                error={qualificationReason?.length === 0 ? {
                  field: 'qualificationReason',
                  error: 'pleaseCompleteThisField'
                } : { field: '', error: '' }}
                secondStepOptions={checkedOptions}
                placeholder='ecosystemTriggerPlaceholder'
              />
            </Col>
          )}
          <Col md={6}>
            <Input
              value={globalExtent}
              onChange={handleChangeGlobalExtent}
              onBlur={handleOnBlurChangeGlobalExtent}
              placeholder='enterGlobalExtent'
              label='ecosystemGlobalExtent'
              field='globalExtent'
              infoTooltip='ecosystemGlobalExtentHint'
              disabled={disabled}
              error={withRelationshipError(
                extentRelationshipErrors,
                'globalExtent',
                checkFields.globalExtent
                  ? { field: '', error: '' }
                  : {
                    field: 'globalExtent',
                    error: 'pleaseCompleteThisField'
                  }
              )}
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Input
              value={extent}
              onChange={handleChangeExtent}
              onBlur={handleOnBlurChangeExtent}
              placeholder='enterSiteMinExtent'
              label='ecosystemExtent'
              field='extentAtSiteMin'
              infoTooltip='ecosystemExtentHint'
              disabled={disabled}
              isOptional={true}
              error={getRelationshipFieldError(extentRelationshipErrors, 'extentAtSiteMin')}
            />
          </Col>
          <Col md={6}>
            <Input
              value={extentBest}
              onChange={handleChangeExtentBest}
              onBlur={handleOnBlurChangeExtentBest}
              placeholder='enterSiteBestEstimateExtent'
              label='ecosystemExtentBest'
              field='extentAtSiteBest'
              infoTooltip='ecosystemExtentBestHint'
              disabled={disabled}
              error={withRelationshipError(
                extentRelationshipErrors,
                'extentAtSiteBest',
                checkFields.extentAtSiteBest
                  ? { field: '', error: '' }
                  : {
                    field: 'extentAtSiteBest',
                    error: 'pleaseCompleteThisField'
                  }
              )}
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <Input
              value={extentMax}
              onChange={handleChangeExtentMax}
              onBlur={handleOnBlurChangeExtentMax}
              placeholder='enterSiteMaxExtent'
              label='ecosystemExtentMax'
              field='extentAtSiteMax'
              infoTooltip='ecosystemExtentMaxHint'
              disabled={disabled}
              isOptional={true}
              error={getRelationshipFieldError(extentRelationshipErrors, 'extentAtSiteMax')}
            />
          </Col>
          <Col md={6}>
            <Input
              value={assessmentDate}
              onChange={handleChangeAssessmentDate}
              placeholder='assessmentDateEcoPlaceholder'
              label='assessmentDateEco'
              field='assessmentDate'
              infoTooltip='ecosystemAssessmentDateHint'
              disabled={disabled}
              error={
                checkFields.assessmentDate
                  ? { field: '', error: '' }
                  : {
                    field: 'assessmentDate',
                    error: 'pleaseCompleteThisField'
                  }
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Textarea
              value={dataSource}
              onChange={handleChangeDataSource}
              onBlur={handleOnBlurChangeDataSource}
              placeholder='enterSourceOfEcosystemData'
              label='source'
              field='ecoSystemDataSource'
              infoTooltip='ecosystemSourceHint'
              height='148px'
              disabled={disabled}
              error={
                checkFields.dataSource
                  ? { field: '', error: '' }
                  : {
                    field: 'ecoSystemDataSource',
                    error: 'pleaseCompleteThisField'
                  }
              }
            />
          </Col>
        </Row>
        <EcosystemReassessmentRationale
          ecosystem={ecosystem}
          site={site}
          reassessmentRational={reassessmentRational}
          handleChangeReassessmentRational={handleChangeReassessmentRational}
          disabled={disabled}
        />
        <EcosystemFormFooter
          ecosystem={ecosystem}
          site={site}
          defaultPage={defaultPage}
          toggleAccordion={toggleAccordion}
        />
      </div>
    </Container>
  )
}

export default EcosystemForm

EcosystemForm.propTypes = {
  item: ecosystemPropType.isRequired,
  updateEcoSystem: PropTypes.func.isRequired,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  toggleAccordion: PropTypes.func,
  defaultPage: PropTypes.bool,
  getEcosystems: PropTypes.func.isRequired
}

EcosystemReassessmentRationale.propTypes = {
  ecosystem: ecosystemPropType.isRequired,
  site: PropTypes.shape({
    reassessmentFlag: PropTypes.bool
  }).isRequired,
  reassessmentRational: PropTypes.string,
  handleChangeReassessmentRational: PropTypes.func.isRequired,
  disabled: PropTypes.bool
}

EcosystemFormFooter.propTypes = {
  ecosystem: ecosystemPropType.isRequired,
  site: PropTypes.shape({
    SiteStatusId: PropTypes.number
  }).isRequired,
  defaultPage: PropTypes.bool,
  toggleAccordion: PropTypes.func
}